/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import cx from 'classnames';
import { find } from 'lodash';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import LucidJS from 'lucidjs';
import PropTypes from 'prop-types';
import Q from 'q';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import videojs, { type AugmentedTextTrack } from 'video.js';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import userAgent from 'js/constants/userAgent';
import { isAiDubbingEnabled } from 'js/lib/MachineTranslationExperiments';
import Retracked from 'js/lib/retracked';
import user from 'js/lib/user';

import { progressCompleted } from 'pages/open-course/common/constants';

/* eslint-disable no-restricted-imports */
import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';
import supportsNativeTextTracks from 'pages/open-course/video/util/supportsNativeTextTracks';

import { InlineNotification } from '@coursera/cds-core';
import type { VideoPlayer as VideoPlayerEventType } from '@coursera/event-pulse-types';
import type { EventData, EventOptions, Events } from '@coursera/event-pulse/sdk';

import type { LearningEffortFirstPlayContextType } from 'bundles/academic-integrity/components/learning-effort/LearningEffortFirstPlayContext';
import { useLearningEffortFirstPlayContext } from 'bundles/academic-integrity/components/learning-effort/LearningEffortFirstPlayContext';
import VideoSkippingDisabledNotification from 'bundles/academic-integrity/components/learning-effort/VideoSkippingDisabledNotification';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import type { QuizQuestionPrompt } from 'bundles/compound-assessments/types/FormParts';
import { useDayOneOnboardingModal } from 'bundles/course-v2/components/day-one-onboarding-modal/DayOneOnboardingModalProvider';
import type { VideoPlayer as VideoPlayerType, VideoRegion } from 'bundles/item-lecture';
import { loadCourseMaterials } from 'bundles/ondemand/actions/CourseMaterialsActions';
import { updateProgress } from 'bundles/ondemand/actions/ProgressActions';
import * as CourseProgressCache from 'bundles/ondemand/stores/CourseProgressCache';
import { useTracker } from 'bundles/page/lib/event-pulse/react';
import Forward10Svg from 'bundles/video-player/assets/svg/Forward10Svg';
import PauseSvg from 'bundles/video-player/assets/svg/PauseSvg';
import PlayArrowSvg from 'bundles/video-player/assets/svg/PlayArrowSvg';
import Replay10Svg from 'bundles/video-player/assets/svg/Replay10Svg';
import VolumeDownSvg from 'bundles/video-player/assets/svg/VolumeDownSvg';
import VolumeOffSvg from 'bundles/video-player/assets/svg/VolumeOffSvg';
import VolumeUpSvg from 'bundles/video-player/assets/svg/VolumeUpSvg';
import type { VideoFeedbackState } from 'bundles/video-player/components/ActionFeedback';
import VideoAnalytics from 'bundles/video-player/components/VideoAnalytics';
import VideoControlsContainer, {
  VIDEO_CONTROLS_CONTAINER_SELECTOR,
} from 'bundles/video-player/components/VideoControlsContainer';
import { styles } from 'bundles/video-player/components/video/__styles__/videoPlayerStyles';
import {
  NOT_SET,
  VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET,
  videoEndPercentage,
  videoStartPlaybackDelay,
} from 'bundles/video-player/constants';
import type {
  GenericVideoPlayerEventingV3UIFunction,
  LayoutMode,
  VideoPlayerEventingV3UIFunction,
} from 'bundles/video-player/types/VideoPlayer';
import type { VideoProgress } from 'bundles/video-player/types/VideoProgress';
import type { ViewOptions } from 'bundles/video-player/types/ViewOptions';
import { createVideoEventTracker } from 'bundles/video-player/utils/AnalyticsMetadataFetcher';
import CrossBrowserFullscreenUtils from 'bundles/video-player/utils/CrossBrowserFullscreenUtils';
import type { OnDemandVideoProgressV1Data } from 'bundles/video-player/utils/OnDemandVideoProgressesAPIUtils';
import OnDemandVideoProgressesAPIUtils from 'bundles/video-player/utils/OnDemandVideoProgressesAPIUtils';
import { sendEvent } from 'bundles/video-player/utils/VideoProgressEventAPIUtils';
import { isHlsEnabled } from 'bundles/video-player/utils/epicFeatureFlags';
import { getInitialResolution } from 'bundles/video-player/utils/resolutionUtils';
import { applySubtitlePosition } from 'bundles/video-player/utils/trackUtils';
import userPreferences from 'bundles/video-player/utils/userPreferences';
import { formatSourcesByResolution } from 'bundles/video-player/utils/videoUtils';
import LearnerInVideoQuiz from 'bundles/video-quiz/components/LearnerInVideoQuiz';
import { getSyncPlayerOnTimeUpdate, syncPlayerDuration, toDecimalSeconds } from 'bundles/video-trim';
import 'bundles/videojs/plugins/cuepoints';
import 'bundles/videojs/plugins/eventEmitter';
import 'bundles/videojs/plugins/heartbeat';
import 'bundles/videojs/plugins/hotkeys';
import 'bundles/videojs/plugins/joinEvents';
import 'bundles/videojs/plugins/resolutions';
import 'bundles/videojs/plugins/waitEvents';
import { handleVideoJsError } from 'bundles/videojs/utils';

import _t from 'i18n!nls/video-player';

import 'css!bundles/videojs/css/coursera';
import 'css!video.js/dist/video-js.css';

/* eslint-enable no-restricted-imports */

export type VideoPlayerOnEndedParams = {
  itemId: string;
  isAutoPlayEnabled: boolean;
  isFullScreenEnabled: boolean;
};

export type Props = {
  viewOptions: ViewOptions;
  setVideoRegion: (region: VideoRegion) => void;
  onChangeFullScreen?: (isFullscreen: boolean) => void;
  ariaLabel?: string;
  visibility?: boolean;
  itemMetadata: ItemMetadata;
  isFullscreen: boolean;
  layoutMode?: LayoutMode;
  isContextMenuEnabled: boolean;
  autoNextCountdownSeconds?: number;
  onVideoPlayedToEnd: (props: VideoPlayerOnEndedParams) => void;
  /** Will defer autoplay until this flag is turned to false */
  deferAutoplayListener?: boolean;
  /**
   * whether autoplaying next item is temporarily disabled
   * e.g. when Coach chat is open [COACH-501]
   */
  isAutoNextPaused?: boolean;
  trackVideoPlayerEvent: <K extends keyof Events>(eventName: K, data: EventData<K>, options?: EventOptions<K>) => void;
};

type State = {
  videoProgress: VideoProgress | null;
  showFeedback: boolean;
  videoFeedbackState?: VideoFeedbackState;
  showInitialCenterPlayButton: boolean;
  subtitlesEnabled: boolean;
};

type ContextProps = Omit<LearningEffortFirstPlayContextType, 'onVideoPlayedToEnd'>;

const VIDEO_PLAYER_SENTRY_SOURCE = 'VideoPlayer(corelex)';

const defaultOptions = {
  volumeStep: 0.1,
  seekStep: 10,
  enableMute: true,
  enableFullscreen: true,
  playbackStep: 0.25,
  playbackLowerLimit: 0.75,
  playbackUpperLimit: 2,
};

export class VideoPlayer extends React.Component<Props & ContextProps, State> {
  player: VideoPlayerType = undefined;

  declare track: (name: string, data?: {}) => void;

  declare videoNode: HTMLVideoElement | null | undefined;

  playerRef = React.createRef<HTMLDivElement>();

  eventingVideoMetadata: VideoPlayerEventType = { videoId: '' };

  hlsSource: string | undefined;

  enableHlsExperiment: boolean = isHlsEnabled();

  enableAiDubbingExperiment: boolean = isAiDubbingEnabled(this.props.viewOptions.course.get('id'));
  actionFeedbackTimeoutRef: NodeJS.Timeout | null = null;

  initialResolution: string = getInitialResolution({
    enableHlsExperiment: this.enableHlsExperiment,
    isAutoAllowed: true,
  });

  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
    _eventData: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof VideoPlayer.contextTypes>;

  state: State = {
    videoProgress: null,
    showFeedback: false,
    videoFeedbackState: undefined,
    showInitialCenterPlayButton: true,
    subtitlesEnabled: false,
  };

  constructor(props: Props & ContextProps) {
    super(props);

    // When this source is fed into video.js, a GET request to assetMasterPlaylistHls is made, so we add the source parameter
    // so that the service knows to override CODECs that don't work. This allows the HLS source to be played properly in the browser.
    const hlsSourceWithSourceParam = props.viewOptions.videoContent.getHlsSource() + '&source=web';
    this.hlsSource = isHlsEnabled() ? hlsSourceWithSourceParam : undefined;
  }

  componentDidMount() {
    const {
      viewOptions: { setVideoPlayer, videoContent, courseProgress },
      setVideoRegion,
      checkLearnerVideoProgress,
    } = this.props;
    const itemId = videoContent.itemMetadata.getId();
    const itemProgress = courseProgress.getItemProgress(itemId);
    const learnerProgress = itemProgress.get('progressState');
    const disableSkippingForward = videoContent.disableSkippingForward;

    // Add visibility change event listener for auto pause/play feature
    document.addEventListener('visibilitychange', this.handleTabVisiblityForAutoPlayPause);
    window.addEventListener('blur', this.handleWindowBlur);
    window.addEventListener('focus', this.handleWindowFocus);

    // This functions checks for the learner's progress on the Video and sets shouldDisableSkippingForward depending on the learnerProgress
    checkLearnerVideoProgress(learnerProgress, disableSkippingForward);

    this.player = this.createVideoJSPlayer();
    this.setupVideoPlayerEvents(this.player);
    setVideoPlayer(this.player);
    setVideoRegion(this.videoNode);

    if (this.playerRef?.current) {
      this.playerRef.current.tabIndex = -1;
    }
  }

  componentDidUpdate(prevProps: Readonly<Props & ContextProps>): void {
    // Reenable autoplay if it was deferred
    if (prevProps.deferAutoplayListener && !this.props.deferAutoplayListener && this.props.viewOptions.autoplay) {
      this.player.play();
    }
  }

  // destroy player on unmount
  componentWillUnmount() {
    const {
      viewOptions: { setInteractiveTranscript, setDubbedInteractiveTranscript },
    } = this.props;

    // Remove visibility change event listener for auto pause/play feature
    document.removeEventListener('visibilitychange', this.handleTabVisiblityForAutoPlayPause);
    window.removeEventListener('blur', this.handleWindowBlur);
    window.removeEventListener('focus', this.handleWindowFocus);

    // save video progress
    this.updateProgress();

    document.removeEventListener('keydown', this.handleDocumentKeyDown);

    if (this.player) {
      if (this.player.isFullscreen()) {
        // When we're in fullscreen, the fullscreenchange event is asynchronous and can lead to the videoPlayer getting
        // disposed before all of its events are disposed properly (in particular,
        // https://github.com/webedx-spark/web/blob/0b1c263/static/bundles/videojs/lib/video.v5-8.js#L11012-L11021)
        // leading to a ton of innocuous but noisy errors.
        this.player.one('fullscreenchange', () => {
          this.player.dispose();
        });
        this.player.exitFullscreen();
      } else {
        this.player.dispose();
      }
    }

    if (this.actionFeedbackTimeoutRef) {
      clearTimeout(this.actionFeedbackTimeoutRef);
    }

    setInteractiveTranscript(null);
    setDubbedInteractiveTranscript?.(null);
  }

  togglePlayingState = (isHotkeyShortcut?: boolean) => {
    if (this.player.paused()) {
      this.player.play();
      if (isHotkeyShortcut) {
        this.trackVideoPlayerClickEvent('video_play_hotkey', {});
        this.showVideoPlayerControlsFeedback(<PlayArrowSvg width={60} height={60} />);
      }
    } else {
      this.player.pause();
      if (isHotkeyShortcut) {
        this.trackVideoPlayerClickEvent('video_pause_hotkey', {});
        this.showVideoPlayerControlsFeedback(<PauseSvg width={60} height={60} />);
      }
    }
  };

  // If the learner navigates away from the video item tab, the video will automatically pause and play again when
  // the learner comes back to the tab (only for when the video item hasn't been completed)
  handleTabVisiblityForAutoPlayPause = () => {
    const { shouldDisableSkippingForward } = this.props;
    if (shouldDisableSkippingForward) {
      if (document.hidden) {
        // Pause the video if the tab is not visible
        this.player.pause();
      } else {
        // Play the video if the tab becomes visible again
        this.player.play();
      }
    }
  };

  handleWindowBlur = () => {
    const { shouldDisableSkippingForward } = this.props;
    if (shouldDisableSkippingForward) {
      this.player.pause();
    }
  };

  handleWindowFocus = () => {
    // Play the video if the window regains focus
    const { shouldDisableSkippingForward } = this.props;
    if (shouldDisableSkippingForward) {
      this.player.play();
    }
  };

  // Dismisses the video skipping disabled notification on click of the X button from
  // VideoSkippingDisabledNotification component
  dismissVideoSkippingDisabledNotification = () => {
    const { setShowVideoSkippingDisabledNotification } = this.props;
    setShowVideoSkippingDisabledNotification(false);
  };

  handlePlayerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    const options = this.player.options();

    const { volumeStep: defaultVolumeStep, seekStep: defaultSeekStep } = defaultOptions;
    const { volumeStep = defaultVolumeStep, seekStep = defaultSeekStep } = {
      ...options,
      ...defaultOptions,
    };
    const { shouldDisableSkippingForward, learnerMaxVideoProgressInSeconds, setShowVideoSkippingDisabledNotification } =
      this.props;

    const activeEl = document.activeElement;
    if (activeEl === this.player.el()) {
      // These events must be handled on player keydown to preserve a11y behavior for the rest of the document.
      switch (event.which) {
        // Spacebar toggles play/pause
        case 32:
          event.preventDefault();
          this.togglePlayingState(true);
          break;

        // Enter key toggles play/pause
        case 13:
          event.preventDefault();
          this.togglePlayingState(true);
          break;

        // Seek with the left arrow key
        case 37: {
          event.preventDefault();
          const videoPositionCurrentTime = this.player.currentTime();
          const previousVideoPosition = videoPositionCurrentTime ?? NOT_SET;
          let curTime = videoPositionCurrentTime - seekStep;

          // The flash this.player tech will allow you to seek into negative
          // numbers and break the seekbar, so try to prevent that.
          if (videoPositionCurrentTime <= seekStep) {
            curTime = 0;
          }
          this.player.currentTime(curTime);
          this.trackVideoPlayerClickEvent('video_seek_backward_hotkey', {
            previousVideoPosition,
            videoPosition: curTime,
          });
          this.showVideoPlayerControlsFeedback(<Replay10Svg width={60} height={60} />);
          break;
        }

        // Seek with the right arrow key
        case 39: {
          const videoPositionCurrentTime = this.player.currentTime();
          const previousVideoPosition = videoPositionCurrentTime ?? NOT_SET;
          const newTimeWithSeekStep = videoPositionCurrentTime + seekStep;
          if (!shouldDisableSkippingForward || newTimeWithSeekStep < learnerMaxVideoProgressInSeconds) {
            event.preventDefault();
            const newTime = this.player.currentTime(newTimeWithSeekStep);
            this.player.currentTime(newTime);
            this.trackVideoPlayerClickEvent('video_seek_forward_hotkey', {
              previousVideoPosition,
              videoPosition: newTimeWithSeekStep,
            });
            this.showVideoPlayerControlsFeedback(<Forward10Svg width={60} height={60} />);
          } else {
            setShowVideoSkippingDisabledNotification(true);
          }
          break;
        }

        // Volume up with up arrow
        case 38: {
          event.preventDefault();
          const currentVolume = this.player.volume();
          const previousVolume = currentVolume ?? NOT_SET;
          const newVolumeWithSeekStep = currentVolume + volumeStep;

          this.player.volume(newVolumeWithSeekStep);
          this.trackVideoPlayerClickEvent('video_volume_increase_hotkey', {
            previousVolume,
            volume: newVolumeWithSeekStep,
          });
          this.showVideoPlayerControlsFeedback(<VolumeUpSvg width={60} height={60} />);
          break;
        }

        // Volume down with down arrow
        case 40: {
          event.preventDefault();
          const currentVolume = this.player.volume();
          const previousVolume = currentVolume ?? NOT_SET;
          const newVolumeWithSeekStep = currentVolume - volumeStep;

          this.player.volume(newVolumeWithSeekStep);
          this.trackVideoPlayerClickEvent('video_volume_decrease_hotkey', {
            previousVolume,
            volume: newVolumeWithSeekStep,
          });
          this.showVideoPlayerControlsFeedback(
            newVolumeWithSeekStep > 0 ? (
              <VolumeDownSvg width={60} height={60} />
            ) : (
              <VolumeOffSvg width={60} height={60} />
            )
          );
          break;
        }

        default:
          break;
      }
    }
  };

  // toggle fullscreen mode on mouse double-click
  handlePlayerDblClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.preventDefault();

    // Skip triggering full-screen toggle if the click was NOT on the video controls container (which currently overlays the entire video).
    // This prevents double clicks on submenus (settings menu, subtitle menu) and IVQs from toggling full-screen accidentally. In the future,
    // we should aim to use a ref instead of a selector. Ticket: https://coursera.atlassian.net/browse/CLE-2193
    if (!(event.target as HTMLElement).matches(VIDEO_CONTROLS_CONTAINER_SELECTOR)) {
      return;
    }

    const options = this.player.options();
    const { enableFullscreen: defaultEnableFullscreen } = defaultOptions;
    const { enableFullscreen = defaultEnableFullscreen } = {
      ...options,
      ...defaultOptions,
    };

    if (enableFullscreen) {
      if (CrossBrowserFullscreenUtils.isFullScreen()) {
        CrossBrowserFullscreenUtils.requestFullScreenExit();
        this.trackVideoPlayerClickEvent('video_full_screen_double_click_hotkey', { fullscreen: false });
      } else {
        CrossBrowserFullscreenUtils.requestFullScreen(this.player?.el_);
        this.trackVideoPlayerClickEvent('video_full_screen_double_click_hotkey', { fullscreen: true });
      }
    }
  };

  handleDocumentKeyDown = (event: KeyboardEvent) => {
    const options = this.player.options();
    const usesModifiers = event.ctrlKey || event.altKey || event.metaKey;

    // Seeing none of our shortcuts use modifiers,
    // we should just return if any are being used.
    // This prevents conflicts with browser defaults like Ctrl+F.
    if (usesModifiers) {
      return;
    }

    const {
      enableMute: defaultEnableMute,
      enableFullscreen: defaultEnableFullscreen,
      playbackStep: defaultPlaybackStep,
      playbackLowerLimit: defaultPlaybackLowerLimit,
      playbackUpperLimit: defaultPlaybackUpperLimit,
    } = defaultOptions;

    const {
      enableMute = defaultEnableMute,
      enableFullscreen = defaultEnableFullscreen,
      playbackStep = defaultPlaybackStep,
      playbackLowerLimit = defaultPlaybackLowerLimit,
      playbackUpperLimit = defaultPlaybackUpperLimit,
    } = {
      ...options,
      ...defaultOptions,
    };

    const target = (event.target as HTMLElement) || (event.srcElement as HTMLElement);
    const { tagName } = target;

    const isContentEditable =
      tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA' || target.isContentEditable;
    if (isContentEditable) {
      return;
    }

    switch (event.which) {
      // Toggle play/pause with the k key
      case 75:
        event.preventDefault();
        if (this.player.paused()) {
          this.player.play();
          this.trackVideoPlayerClickEvent('video_play_hotkey', {});
          this.showVideoPlayerControlsFeedback(<PlayArrowSvg width={60} height={60} />);
        } else {
          this.player.pause();
          this.trackVideoPlayerClickEvent('video_pause_hotkey', {});
          this.showVideoPlayerControlsFeedback(<PauseSvg width={60} height={60} />);
        }
        break;

      // Toggle mute with the m key
      case 77:
        event.preventDefault();
        if (enableMute) {
          this.player.muted(!this.player.muted());
          this.trackVideoPlayerClickEvent('video_mute_hotkey', {});
          this.showVideoPlayerControlsFeedback(
            this.player.muted() ? <VolumeOffSvg width={60} height={60} /> : <VolumeUpSvg width={60} height={60} />
          );
        }
        break;

      // Toggle fullscreen with the f key
      case 70:
        event.preventDefault();
        if (enableFullscreen) {
          if (CrossBrowserFullscreenUtils.isFullScreen()) {
            CrossBrowserFullscreenUtils.requestFullScreenExit();
            this.trackVideoPlayerClickEvent('video_full_screen_hotkey', { fullscreen: false });
          } else {
            CrossBrowserFullscreenUtils.requestFullScreen(this.player?.el_);
            this.trackVideoPlayerClickEvent('video_full_screen_hotkey', { fullscreen: true });
          }
        }
        break;

      // Increase playback rate with Shift + >
      case 190: {
        event.preventDefault();
        const currentPlaybackRate = this.player.playbackRate();
        if (event.shiftKey && currentPlaybackRate < playbackUpperLimit) {
          const previousPlaybackRate = currentPlaybackRate ?? NOT_SET;
          const newPlaybackRate = currentPlaybackRate + playbackStep;

          this.player.playbackRate(newPlaybackRate);
          this.trackVideoPlayerClickEvent('video_playback_rate_increase_hotkey', {
            previousPlaybackRate,
            playbackRate: newPlaybackRate,
          });
        }
        break;
      }
      // Decrease playback rate with Shift + <
      case 188: {
        event.preventDefault();
        const currentPlaybackRate = this.player.playbackRate();
        if (event.shiftKey && currentPlaybackRate > playbackLowerLimit) {
          const previousPlaybackRate = currentPlaybackRate ?? NOT_SET;
          const newPlaybackRate = currentPlaybackRate - playbackStep;

          this.player.playbackRate(newPlaybackRate);
          this.trackVideoPlayerClickEvent('video_playback_rate_decrease_hotkey', {
            previousPlaybackRate,
            playbackRate: newPlaybackRate,
          });
        }
        break;
      }
      default:
        break;
    }
  };

  initializeSubtitleTracks = () => {
    const track = find(this.player.textTracks(), (t) => t.language === userPreferences.get('subtitleLanguage'));
    if (track) {
      track.mode = 'showing';
    }
    this.player.trigger('trackloaded');
  };

  setupTrimmedDurationSeconds = () => {
    syncPlayerDuration(this.player, this.props.viewOptions.videoContent.trimmedDurationSeconds);
  };

  onLoadedMetadata = () => {
    this.setUserPreferences();
    this.initializeSubtitleTracks();
    this.attachVideoEndedListener();

    this.attachVideoProgressCuepoints();
    this.setupTrimmedDurationSeconds();
  };

  /**
   * Creates the initial VideoJS player object
   */
  createVideoJSPlayer = () => {
    const {
      viewOptions: { videoContent, nextItemMetadataOrItemGroup, course },
      layoutMode = 'fluid',
    } = this.props;

    const { name, itemMetadata } = videoContent;

    // Although we have this stored in this.initialResolution, we don't want to include "auto" when passing this into the resolutions plugin,
    // since that plugin only holds the actual resolution values.
    const resolution = getInitialResolution({
      enableHlsExperiment: this.enableHlsExperiment,
      isAutoAllowed: false,
    });

    const layoutModeForOptions = layoutMode === 'fluid' ? { fluid: true } : { fill: true };
    const areNativeTextTracksSupported = supportsNativeTextTracks();
    const enableHlsAndHlsSourceExists = this.enableHlsExperiment && !!this.hlsSource;

    const videoJsOptions = {
      nextItemMetadataOrItemGroup,
      title: name,
      subtitle: course.get('name'),
      courseId: course.get('id'),
      itemId: itemMetadata.get('id'),
      moduleName: itemMetadata.get('module.name'),
      moduleId: itemMetadata.get('module.id'),
      lessonId: itemMetadata.get('lesson.id'),
      lessonName: itemMetadata.get('lesson.name'),
      defaultSubtitleLanguage: userPreferences.get('subtitleLanguage'),
      controls: false,
      html5: {
        // In order for us to detect when the resolution change is visually rendered using the segmentMetadata track when we use HLS, we need to set
        // nativeTextTracks to false. If this is set to true, Chrome and other browsers do not give you access to the segment metadata track.
        nativeTextTracks: enableHlsAndHlsSourceExists ? false : areNativeTextTracksSupported,
        nativeAudioTracks: false,
        nativeVideoTracks: false,
        vhs: {
          overrideNative: true,
          limitRenditionByPlayerDimensions: true,
          useDevicePixelRatio: true,
          customPixelRatio: 2,
        },
      },
      poster: videoContent.getPosterForResolution('720p'),
      plugins: {
        hotkeys: {},
        resolutions: {
          sourcesByResolution: formatSourcesByResolution(videoContent.sources),
          resolution,
        },
        joinEvents: {},
        waitEvents: userAgent.browser.name !== 'IE' ? {} : undefined,
      },
    };

    const videoJsOptionsFinal = { ...videoJsOptions, ...layoutModeForOptions };

    const player = videojs(this.videoNode as Element, videoJsOptionsFinal);

    // @ts-expect-error TSMIGRATION
    if (!player.emitter) {
      // All custom events should be sent on the Lucid emitter

      // @ts-expect-error TSMIGRATION
      player.emitter = LucidJS.emitter();
    }

    // @ts-expect-error TSMIGRATION
    player.setupHeartbeatListener();
    // @ts-expect-error TSMIGRATION
    player.currentTrack = function () {
      return (
        player
          // @ts-expect-error TSMIGRATION
          .textTracks()
          .tracks_.find((track: $TSFixMe) => track.mode === 'showing')
      );
    };

    return player;
  };

  handleTimeUpdate = () => {
    const { trimmedDurationSeconds } = this.props.viewOptions.videoContent;
    // it seems when the last HLS segment is loaded, the player duration is reset to the manifest (but durationChange event is not emitted).
    if (trimmedDurationSeconds && trimmedDurationSeconds !== this.player.duration()) {
      this.setupTrimmedDurationSeconds();
    }

    this.syncPlayer();

    this.props.setLearnerMaxVideoProgressInSeconds(
      Math.max(this.props.learnerMaxVideoProgressInSeconds, this.player.currentTime())
    );
  };

  /**
   * Sets up various listeners for a given VideoJS player
   *
   * @param {any} player The VideoJS player whose listeners to setup
   */
  setupVideoPlayerEvents = (player: VideoPlayerType) => {
    const {
      viewOptions: {
        videoContent: { subtitlesVtt, dubbedSubtitlesVtt, sources },
        setInteractiveTranscript,
        setDubbedInteractiveTranscript,
        course,
      },
    } = this.props;

    const initialMp4Source = sources?.[this.initialResolution]?.['video/mp4'];

    player.ready(() => {
      const primaryLanguageCodes = course.get('primaryLanguageCodes');

      setInteractiveTranscript({
        player,
        languageMap: subtitlesVtt,
        defaultLanguage: primaryLanguageCodes && primaryLanguageCodes[0],
      });
      setDubbedInteractiveTranscript?.({
        player,
        languageMap: { ...subtitlesVtt, ...dubbedSubtitlesVtt },
        defaultLanguage: primaryLanguageCodes && primaryLanguageCodes[0],
      });

      // This will be defined, but I need to optionally access this so tests don't break. It's not great, but it's necessary
      // for now to avoid having to rewrite this entire component at this stage.
      player.on('waiting', () => {
        player.addClass('vjs-waiting');
      });

      if (this.enableHlsExperiment && this.hlsSource && this.initialResolution === 'auto') {
        player.src({
          src: this.hlsSource,
          type: 'application/x-mpegURL',
        });
      } else {
        player.src({
          src: initialMp4Source,
          type: 'video/mp4',
        });
      }
    });

    player.on('error', () => {
      handleVideoJsError(player, VIDEO_PLAYER_SENTRY_SOURCE);
    });

    player.on('contextmenu', (e: Event) => {
      const { isContextMenuEnabled } = this.props;
      if (!isContextMenuEnabled) {
        e.preventDefault();
      }
    });

    player.on('loadedmetadata', this.onLoadedMetadata);
    player.on('durationchange', this.setInitialPlaybackTime);

    player.on('play', () => {
      const textTracks = Array.from(this.player.textTracks()) as AugmentedTextTrack[];
      applySubtitlePosition({ textTracks });
    });

    player.on('timeupdate', this.handleTimeUpdate);

    // TODO: Uncomment these lines once we figure out how to process high
    // traffic.
    // We have turned this off during the coronavirus outbreak due to increased
    // strain on courral from the OnDemandVideoProgresses API.
    // player.emitter.on('heartbeat:30000', this.updateProgress);
    // player.on('scrubbed', this.updateProgress);

    player.on('keydown', this.handlePlayerKeyDown);

    document.addEventListener('keydown', this.handleDocumentKeyDown);
    player.on('canplay', () => {
      this.syncPlayer = getSyncPlayerOnTimeUpdate({
        player: this.player,
        trimmedDurationSeconds: this.props.viewOptions.videoContent.trimmedDurationSeconds,
        trimmedStartTimeSeconds: this.props.viewOptions.videoContent.trimmedStartTimeSeconds,
      });
    });
  };

  /**
   * Start the video playback at a specific time when it's loaded by calling currentTime
   * calling currentTime is only allowed when the video's duration becomes known.
   * the event that this triggers is 'durationchange'
   */
  setInitialPlaybackTime = () => {
    const {
      viewOptions: { course, videoContent, startPlaybackSeconds },
    } = this.props;

    // Only run this once so we don't overwrite the learner's position if they scrub.
    this.player.off('durationchange', this.setInitialPlaybackTime);
    const trimmedStartTimeSeconds = videoContent.trimmedStartTimeSeconds ?? 0;
    const trimmedDurationSeconds = videoContent.trimmedDurationSeconds;

    if (startPlaybackSeconds !== undefined) {
      // Start playback from the specified timestamp.
      this.setPlaybackTime(startPlaybackSeconds);
    } else {
      // Start playback from where the learner last left off
      const { videoId } = videoContent;
      const courseId = course.get('id');

      OnDemandVideoProgressesAPIUtils.get(courseId, videoId)
        .then((progressData: OnDemandVideoProgressV1Data) => {
          return progressData.elements[0];
        })
        .fail(() => {
          if (user.isAuthenticatedUser()) {
            return {
              courseId,
              videoId,
              userId: user.get().id,
              viewedUpTo: trimmedStartTimeSeconds,
            };
          }
          return {};
        })
        .done((progress) => {
          const { viewedUpTo = 0 } = progress;

          let viewedUpToSeconds = toDecimalSeconds(viewedUpTo / 1000); // convert from ms to s
          if (trimmedStartTimeSeconds > viewedUpToSeconds) {
            viewedUpToSeconds = trimmedStartTimeSeconds;
          }
          if (trimmedDurationSeconds) {
            const offsetEnd = trimmedDurationSeconds + trimmedStartTimeSeconds;
            if (viewedUpToSeconds > offsetEnd) {
              viewedUpToSeconds = offsetEnd;
            }
          }

          this.setState({
            videoProgress: {
              ...progress,
              viewedUpTo: viewedUpToSeconds * 1000,
            },
          });

          this.setPlaybackTime(viewedUpToSeconds, !trimmedStartTimeSeconds);
          this.trackInitialVideoPlayerEvent(viewedUpToSeconds);
        });
    }
  };

  syncPlayer = getSyncPlayerOnTimeUpdate({
    player: this.player,
    trimmedDurationSeconds: this.props.viewOptions.videoContent.trimmedDurationSeconds,
    trimmedStartTimeSeconds: this.props.viewOptions.videoContent.trimmedStartTimeSeconds,
  });

  /**
   * Updates player preferences based on stored
   * user preferences
   */
  setUserPreferences = () => {
    const playbackRate = userPreferences.get('playbackRate');
    const volume = userPreferences.get('volume');

    this.player.playbackRate(playbackRate);
    this.player.volume(volume);
  };

  /**
   * Sets the player's playback time
   *
   * @param {number} newTime The new time to set
   */
  setPlaybackTime(newTime: number, applyVideoStartPlaybackDelay = true) {
    // Add some delay, but have the delayed time be at least 0
    const delayedNewTime = Math.max(0, newTime - (applyVideoStartPlaybackDelay ? videoStartPlaybackDelay : 0));

    this.player.currentTime(delayedNewTime);
  }

  /**
   * Updates the stored video progress based on
   * how much of the video a learner has watched
   */
  updateProgress = () => {
    const {
      viewOptions: { course, videoContent },
    } = this.props;

    const videoLength = this.player.duration();
    const currentTime = this.player.currentTime();

    const endzoneStart = videoLength * videoEndPercentage;
    // This is the current time, relative to the entire video length (ie no offset)
    let viewedUpTo = parseInt((this.player.currentTime() * 1000).toString(), 10); // need to pass time in ms
    if (currentTime > endzoneStart) {
      viewedUpTo = 0;
    }

    const { videoId } = videoContent;
    const courseId = course.get('id');
    OnDemandVideoProgressesAPIUtils.update(courseId, videoId, viewedUpTo);
  };

  getScrollContainer() {
    return document.querySelector('[data-id="item-scroll-container"]');
  }

  getOffsetCuePoint = (percentage: number) => {
    const {
      viewOptions: {
        videoContent: { trimmedDurationSeconds, trimmedStartTimeSeconds },
      },
    } = this.props;
    const offsetStart = trimmedStartTimeSeconds ?? 0;
    const videoLength = trimmedDurationSeconds ?? this.player.duration();
    return offsetStart + videoLength * percentage;
  };

  /**
   * Updates the video progress on 25%, 50%, 75% mark
   */
  attachVideoProgressCuepoints = () => {
    // Add cue points at 25%, 50%, and 75% of the video length
    [0.25, 0.5, 0.75].forEach((percentage) => {
      const cuePoint = this.getOffsetCuePoint(percentage);
      this.player.addCuepoint({ at: cuePoint, visible: true }, () => {
        this.updateProgress();
      });
    });
  };

  /**
   * Attaches an event listeners that waits for
   * the video to finish
   */
  attachVideoEndedListener = () => {
    const endzoneStart = this.getOffsetCuePoint(videoEndPercentage);
    const endzoneEnd = this.getOffsetCuePoint(100);

    const setupCuepoint = () => {
      let executed = false;
      return () => {
        if (!executed) {
          executed = true;
          const {
            viewOptions: { videoContent, courseProgress },
            itemMetadata,
          } = this.props;

          const itemId = videoContent.itemMetadata.getId();
          const itemProgress = courseProgress.getItemProgress(itemId);

          // The video progress API is asynchronous and can result in inconsistency,
          // Need to optimistically set progress in-memory, and update
          // through the API in the background.
          //
          // If the learner moves to another page, the item progress has been updated
          // in-memory. If the learner refreshes, hopefully the progress API will
          // have finished updating and their new progress will be loaded.
          Q(sendEvent(videoContent, 'ended'))
            .then((response) => {
              if (itemProgress && response && response.itemProgress) {
                const { executeAction } = this.context;

                // optimistically set video progress to completed in-memory
                const videoItemProgress = { ...response.itemProgress, progressState: progressCompleted };
                itemProgress.set(videoItemProgress);

                executeAction(updateProgress, {
                  courseProgress,
                });

                // refresh the course material data so that the locked items can change without refresh
                const courseSlug = itemMetadata.get('course').get('slug') as string;
                executeAction(loadCourseMaterials, { courseSlug, refetch: true });

                CourseProgressCache.saveProgressToLocalStorage(courseProgress.get('id'), courseProgress);
              }
            })
            .done();

          const {
            _eventData: {
              namespace: { app },
            },
          } = this.context;
          const tracker = createVideoEventTracker(app, this.player);

          tracker('end');
        }
      };
    };

    this.player.addCuepoint(
      {
        at: endzoneStart,
        to: endzoneEnd,
      },
      setupCuepoint()
    );

    const { onVideoPlayedToEnd } = this.props;
    this.player.one('ended', () => {
      const itemId: string = this.props.itemMetadata.get('id');
      const isAutoPlayEnabled = userPreferences && userPreferences.get('autoplay');
      const isFullScreenEnabled = CrossBrowserFullscreenUtils.isFullScreen();
      onVideoPlayedToEnd({ itemId, isAutoPlayEnabled, isFullScreenEnabled });
    });
  };

  setupEventingVideoMetadata = (): VideoPlayerEventType => {
    const {
      viewOptions: { videoContent, course },
      learnerMaxVideoProgressInSeconds,
    } = this.props;

    const { name, itemMetadata, dubbedSources } = videoContent;

    const audioDubbingLanguage = userPreferences.get('audioLanguage');
    const audioDubbingEnabled = audioDubbingLanguage !== 'none';

    const audioDubbingSources = dubbedSources || {};
    const audioDubbingAvailable = Object.keys(audioDubbingSources).length > 0;

    const videoPlayerEventingMetadata: VideoPlayerEventType = {
      videoId: itemMetadata.get('id'),
      autoplay: userPreferences.get('autoplay'),
      courseId: course.get('id'),
      courseName: course.get('name'),
      courseSlug: course.get('slug'),
      fullscreen: CrossBrowserFullscreenUtils.isFullScreen(),
      lessonId: itemMetadata.get('lesson.id'),
      lessonName: itemMetadata.get('lesson.name'),
      moduleId: itemMetadata.get('module.id'),
      moduleName: itemMetadata.get('module.name'),
      playbackRate: userPreferences.get('playbackRate'),
      resolution: this.initialResolution,
      subtitleLanguage: audioDubbingEnabled
        ? userPreferences.get('dubbedSubtitleLanguage')
        : userPreferences.get('subtitleLanguage'),
      audioLanguage: userPreferences.get('audioLanguage'),
      audioLanguageEnabled: userPreferences.get('audioLanguage') !== VIDEO_PLAYER_AUDIO_LANGUAGE_NOT_SET,
      audioDubbingAvailable,
      videoDuration: itemMetadata.get('timeCommitment') / 1000,
      // @ts-expect-error There is a typescript issue with getting the VERSION constant from the videojs library
      // that has many threads about it online. The general recommendation is to ignore the TS error
      videoLibraryVersion: String(videojs.VERSION),
      videoName: name,
      videoPosition: learnerMaxVideoProgressInSeconds ? Math.round(learnerMaxVideoProgressInSeconds) : NOT_SET,
      volume: userPreferences.get('volume'),
      trimmedStartTime: videoContent.trimmedStartTimeSeconds,
      trimmedDuration: videoContent.trimmedDurationSeconds ?? undefined,
    };

    return videoPlayerEventingMetadata;
  };

  trackVideoPlayerEvent: GenericVideoPlayerEventingV3UIFunction = (eventName, buttonName, videoPlayerMetadata) => {
    const { trackVideoPlayerEvent } = this.props;

    this.eventingVideoMetadata = this.setupEventingVideoMetadata();

    trackVideoPlayerEvent(eventName, {
      button: { name: buttonName },
      videoPlayer: { ...this.eventingVideoMetadata, ...videoPlayerMetadata },
    });
  };

  /**
   * Uses the eventing v3 track function to fire click button events with the current video metatdata
   * @param {ButtonName} buttonName The eventing v3 click button name that will be fired
   * @param {Partial<VideoPlayerEventType>} videoPlayerMetadata Parameter to easily update video metadata sent with the event. Only partial data is needed and the rest will be decontructed
   * with the existing eventingVideoMetadata object
   */
  trackVideoPlayerClickEvent: VideoPlayerEventingV3UIFunction = (buttonName, videoPlayerMetadata) => {
    this.trackVideoPlayerEvent('click_button', buttonName, videoPlayerMetadata);
  };

  /**
   * Uses the eventing v3 track function to fire view button events with the current video metatdata
   * @param {ButtonName} buttonName The eventing v3 click button name that will be fired
   * @param {Partial<VideoPlayerEventType>} videoPlayerMetadata Parameter to easily update video metadata sent with the event. Only partial data is needed and the rest will be decontructed
   * with the existing eventingVideoMetadata object
   */
  trackVideoPlayerViewEvent: VideoPlayerEventingV3UIFunction = (buttonName, videoPlayerMetadata) => {
    this.trackVideoPlayerEvent('view_button', buttonName, videoPlayerMetadata);
  };

  /**
   * Uses the eventing v3 track function to fire a view button events with the current video metatdata
   * @param {number} videoPosition The video position in seconds
   * with the existing eventingVideoMetadata object
   */
  trackInitialVideoPlayerEvent = (videoPosition: number) => {
    const { trackVideoPlayerEvent } = this.props;

    this.eventingVideoMetadata = this.setupEventingVideoMetadata();

    trackVideoPlayerEvent('view_button', {
      button: { name: 'video_player' },
      videoPlayer: { ...this.eventingVideoMetadata, videoPosition },
    });
  };

  showVideoPlayerControlsFeedback = (icon: JSX.Element) => {
    const { showInitialCenterPlayButton } = this.state;
    if (showInitialCenterPlayButton) return;

    this.setState({ videoFeedbackState: { icon }, showFeedback: true });

    if (this.actionFeedbackTimeoutRef) {
      clearTimeout(this.actionFeedbackTimeoutRef);
    }

    this.actionFeedbackTimeoutRef = setTimeout(() => {
      this.setState({ showFeedback: false });
    }, 1000);
  };

  displayInitialCenterPlayButton = (showInitialCenterPlayButton: boolean) => {
    this.setState({ showInitialCenterPlayButton });
  };

  // wrap the player in a div with a `data-vjs-player` attribute
  // so videojs won't create additional wrapper in the DOM
  // see https://github.com/videojs/video.js/pull/3856
  render() {
    const {
      viewOptions,
      viewOptions: { nextItemMetadataOrItemGroup },
      ariaLabel,
      visibility,
      itemMetadata,
      isFullscreen,
      layoutMode = 'fluid',
      autoNextCountdownSeconds,
      showVideoSkippingDisabledNotification,
      isAutoNextPaused,
    } = this.props;
    const optionsWithPlayer = { ...viewOptions, videoPlayer: this.player };
    const { videoContent, videoQuizModel, autoplay } = optionsWithPlayer;
    const { questions = [] } = videoQuizModel || {};

    const { videoProgress, videoFeedbackState, showFeedback, showInitialCenterPlayButton } = this.state;

    const sources = videoContent.getSources();
    const { subtitlesVtt } = videoContent;

    // currently, new asset service subtitle proxy has full URLs
    // let's strip the domain, until we can figure out complicated CORS issues
    // we have with our video player/subtitles/posters/videos
    // TODO: (billy) move this into the new model after rebase.
    for (const language in subtitlesVtt) {
      if (subtitlesVtt[language] && subtitlesVtt[language].url) {
        // eslint-disable-next-line no-useless-escape
        subtitlesVtt[language].url = subtitlesVtt[language].url.replace(/^https?:\/\/[^\/]+\//, '/');
      }
    }

    const poster = autoplay ? undefined : videoContent.getPosterForResolution('720p');
    const webmSource = sources?.['video/webm'];
    const containerStyle = layoutMode === 'fill' ? { width: '100%', height: '100%' } : {};
    const videoClassName = cx(
      'vjs-react',
      'c-video',
      { 'vjs-fluid': layoutMode === 'fluid' },
      'vjs-circle-play-centered',
      'vjs-coursera-skin',
      'vjs-coursera-phoenix-skin',
      'video-js',
      { 'vjs-fill': layoutMode === 'fill' }
    );

    const enableHlsAndHlsSourceExists = this.enableHlsExperiment && !!this.hlsSource;
    const courseSlug = itemMetadata.get('course').get('slug') as string;
    const itemId = itemMetadata.get('id');

    return (
      <div
        css={styles.videoPlayer}
        style={{ position: 'relative', ...containerStyle }}
        onDoubleClick={this.handlePlayerDblClick}
      >
        <div data-vjs-player ref={this.playerRef}>
          {videoQuizModel && courseSlug && itemId && (
            <LearnerInVideoQuiz
              itemId={itemId}
              courseSlug={courseSlug}
              videoQuizModel={videoQuizModel}
              player={this.player}
            />
          )}

          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            className={videoClassName}
            crossOrigin="anonymous"
            preload="auto"
            ref={(node) => {
              this.videoNode = node;
            }}
            poster={poster}
            autoPlay={autoplay}
            tabIndex={0}
            aria-label={ariaLabel}
          >
            {enableHlsAndHlsSourceExists && (
              <source key={this.hlsSource} type="application/x-mpegURL" src={this.hlsSource} />
            )}

            {Object.keys(sources)
              .filter((format) => format !== 'video/webm')
              .map((type) => {
                const src = sources[type];
                return <source key={src} type={type} src={src} />;
              })}

            {webmSource && <source key={sources[webmSource]} type="video/webm" src={webmSource} />}

            {Object.keys(videoContent.dubbedSubtitlesVtt).map((languageCode) => {
              const sub = videoContent.dubbedSubtitlesVtt[languageCode];
              return (
                <track
                  key={languageCode}
                  /**
                   * Track tag doesn't support introducing a new property name isDubbed that can help distinguish between
                   * dubbed and non-dubbed subtitles. So we are taking help of id attribute to distinguish between the two.
                   * We are suffixing the id with '-dub' to identify dubbed subtitles.
                   * This suffix is used in useSubtitleMenuControls hook to set the track as showing for dubbed subtitles.
                   */
                  id={languageCode + '-dub'}
                  kind="captions"
                  label={_t(sub.label)}
                  src={sub.url}
                  srcLang={languageCode}
                />
              );
            })}

            {Object.keys(subtitlesVtt).map((languageCode) => {
              const sub = subtitlesVtt[languageCode];
              return (
                <track key={languageCode} kind="captions" label={_t(sub.label)} src={sub.url} srcLang={languageCode} />
              );
            })}
          </video>

          {/* Wrapping the notification in an aria-live region: role - "alert" so that assistive technologies can announce the notification */}
          <div role="alert">
            {showVideoSkippingDisabledNotification && (
              <VideoSkippingDisabledNotification onDismiss={this.dismissVideoSkippingDisabledNotification} />
            )}
          </div>
          {this.player && (
            <VideoControlsContainer
              enableHls={enableHlsAndHlsSourceExists}
              hlsSource={this.hlsSource}
              defaultSubtitleLanguage={userPreferences.get('subtitleLanguage')}
              nextItem={nextItemMetadataOrItemGroup}
              originalSources={viewOptions.videoContent.sources}
              dubbedSources={viewOptions.videoContent.dubbedSources}
              dubbedSubtitlesVtt={viewOptions.videoContent.dubbedSubtitlesVtt}
              player={this.player}
              videoQuizQuestions={questions}
              onChangeFullScreen={(isFullscreenInput) => {
                if (isFullscreenInput) {
                  CrossBrowserFullscreenUtils.requestFullScreenExit();
                } else {
                  CrossBrowserFullscreenUtils.requestFullScreen(this.player?.el_);
                }
                this.trackVideoPlayerClickEvent('video_full_screen', { fullscreen: !isFullscreenInput });
                this.props.onChangeFullScreen?.(!isFullscreenInput);
              }}
              itemMetadata={itemMetadata}
              visibility={visibility}
              isFullscreen={isFullscreen}
              autoNextCountdownSeconds={autoNextCountdownSeconds}
              isAutoNextPaused={isAutoNextPaused}
              trackVideoPlayerClickEvent={this.trackVideoPlayerClickEvent}
              trackVideoPlayerViewEvent={this.trackVideoPlayerViewEvent}
              trimmedStartTimeSeconds={videoContent.trimmedStartTimeSeconds}
              trimmedDurationSeconds={videoContent.trimmedDurationSeconds}
              videoBoundaryElement={this.playerRef.current || undefined}
              showFeedback={showFeedback}
              videoFeedbackState={videoFeedbackState}
              displayInitialCenterPlayButton={this.displayInitialCenterPlayButton}
              showInitialCenterPlayButton={showInitialCenterPlayButton}
            />
          )}
          {this.player && <VideoAnalytics player={this.player} videoProgress={videoProgress} />}
        </div>
      </div>
    );
  }
}

const VideoPlayerWithFirstPlay = (props: Props) => {
  const LearningEffortFirstPlayContext = useLearningEffortFirstPlayContext();
  // This function from learning effort context sets shouldDisableSkippingForward to false when the learner finishes playing the video
  const { onVideoPlayedToEnd: onVideoPlayedToEndFromContext, ...rest } = LearningEffortFirstPlayContext;

  const { onVideoPlayedToEnd: onVideoPlayedToEndFromProps, ...otherProps } = props;

  return (
    <VideoPlayer
      {...otherProps}
      {...rest}
      onVideoPlayedToEnd={(params) => {
        onVideoPlayedToEndFromProps(params);
        onVideoPlayedToEndFromContext();
      }}
    />
  );
};

/**
 * Since the onboarding container just shows on top of the first video, if autoplay
 * is enabled it will play under the onboarding container. So we disable autoplay
 * when the onboarding container is open and re-enable it when it's closed.
 */
const VideoPlayerWithDayOneOnboardingContext = (props: Props) => {
  const { isOpen: isDayOneOnboardingModalOpen } = useDayOneOnboardingModal();
  return (
    <VideoPlayerWithFirstPlay
      {...props}
      viewOptions={{
        ...props.viewOptions,
        autoplay: props.viewOptions.autoplay && !isDayOneOnboardingModalOpen,
      }}
      deferAutoplayListener={isDayOneOnboardingModalOpen}
    />
  );
};

const VideoPlayerWithV3Eventing = (props: Omit<Props, 'trackVideoPlayerEvent'>) => {
  const track = useTracker();
  return (
    <ErrorBoundaryWithTagging
      componentName={VIDEO_PLAYER_SENTRY_SOURCE}
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t(
            'An error was encountered while playing this video, and our development team has been alerted. Please try again later.'
          )}
        </InlineNotification>
      }
    >
      <VideoPlayerWithDayOneOnboardingContext {...props} trackVideoPlayerEvent={track} />
    </ErrorBoundaryWithTagging>
  );
};

export default compose<Omit<Props, 'trackVideoPlayerEvent'>, Omit<Props, 'trackVideoPlayerEvent'>>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'open_course.video',
      page: 'item_layout',
    },
  }))
)(VideoPlayerWithV3Eventing);
